import type { FunctionComponent } from 'react';
import { useRef, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { getSkillTitle, type SkillProficiencyLevel, type SkillShort } from '~/shared/data-contract/skill';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '~/ui/core/dropdown-menu/DropdownMenu';
import { IconWarning } from '~/ui/core/icons';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';
import { ProficiencyScale } from '~/ui/domain/skill/ProficiencyScale';
import { SkillBadge } from '~/ui/domain/skill/SkillBadge';

import { useWarningSignalsVisible } from '../../app/common/hooks/use-warning-signals-visible';
import { ProficiencyLevelDropdownMenu, SkillContextMenu } from '../../job-architecture-v2';

export const LearningOpportunitySkillRow: FunctionComponent<{
    readonly isEditable?: boolean;
    readonly languageCode?: string;
    readonly onClick?: (skillId: string, preferredLanguage?: string) => void;
    readonly onLevelChange?: (skillId: string, level: SkillProficiencyLevel) => void;
    readonly onRemove?: (skillId: string) => void;
    readonly skill: SkillShort;
}> = ({ isEditable = false, languageCode, onClick, onLevelChange, onRemove, skill }) => {
    const translate = useTranslate();
    const isWarningSignalsVisible = useWarningSignalsVisible();
    const [isProficiencyOpen, setIsProficiencyOpen] = useState(false);
    const proficiencyTriggerRef = useRef<HTMLButtonElement | null>(null);
    const skillTitle = getSkillTitle(skill, languageCode);

    return (
        <div
            className="flex w-full items-center justify-between gap-l py-s"
            data-testid="learning-opportunity-skill-row"
        >
            <div className="flex items-center gap-2">
                <SkillBadge
                    skill={{ title: skillTitle, type: skill.skillType }}
                    onClick={() => onClick?.(skill.id, languageCode)}
                />
                {isWarningSignalsVisible && skill.isSynced === false && (
                    <Tooltip>
                        <Tooltip.Trigger asChild={true}>
                            <IconWarning
                                className="text-warning-500"
                                data-testid="skill-not-synced-warning"
                                size={16}
                            />
                        </Tooltip.Trigger>
                        <Tooltip.Portal>
                            <Tooltip.Content side="top">
                                {translate('pages.job-architecture-v2.skills.not-synced-tooltip')}
                            </Tooltip.Content>
                        </Tooltip.Portal>
                    </Tooltip>
                )}
            </div>
            <div className="flex items-center gap-s">
                {isEditable ? (
                    <DropdownMenu open={isProficiencyOpen} onOpenChange={setIsProficiencyOpen}>
                        <DropdownMenuTrigger asChild={true}>
                            <button
                                ref={proficiencyTriggerRef}
                                className="cursor-pointer border-0 bg-transparent p-xs"
                                data-testid="learning-opportunity-skill-proficiency"
                                type="button"
                            >
                                <ProficiencyScale active={true} level={skill.level ?? 0} scaleCount={5} />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="center"
                            collisionPadding={16}
                            side="right"
                            sideOffset={8}
                            style={{ overflow: 'hidden', zIndex: 2000 }}
                        >
                            <ProficiencyLevelDropdownMenu
                                activeLevel={skill.level}
                                disabled={false}
                                id={skill.id}
                                onChangeProficiencyLevel={(skillId, level) => {
                                    if (level === null || level === undefined) return;
                                    onLevelChange?.(skillId, level);
                                }}
                            />
                        </DropdownMenuContent>
                    </DropdownMenu>
                ) : (
                    <span data-testid="learning-opportunity-skill-proficiency">
                        <ProficiencyScale active={true} level={skill.level ?? 0} scaleCount={5} />
                    </span>
                )}
                {isEditable && onRemove !== undefined && (
                    <SkillContextMenu
                        onDelete={() => onRemove(skill.id)}
                        onShowSkillDetails={() => onClick?.(skill.id, languageCode)}
                        onTriggerProficiencyDropdown={() => proficiencyTriggerRef.current?.click()}
                    />
                )}
            </div>
        </div>
    );
};
